/**
 * 📦 **The kistill — the chest, and what it will and will not hold.**
 *
 * **D106, T135.** 🎯 **haugur, 2026-08-12**, opening the task: *"we need to have
 * the chests feature for you to store items when inventory gets full."*
 *
 * ## 🎯 The two words are his, and they are the same word
 *
 * **`kistill` is the diminutive of `kista`** — so the single chest is literally
 * *"the little chest"*, and two of them side by side make *"the chest"*. **The
 * language does the explaining**, which is what **D2** wants and what a tooltip
 * cannot.
 *
 * ## 🔴 Why this is NOT the hearth's code, though both are containers
 *
 * A hearth slot stores a **count** of one known thing — logs, then ash — which
 * is why it needed a `woodItem` bolted on the moment a second wood arrived. **A
 * chest slot stores an arbitrary `Stack`**, exactly as a pack slot does. So this
 * reuses `inventory.ts` rather than `hearth.ts`, and a chest is simply *another
 * array of stacks*.
 *
 * ## 📏 27, and the number is not the finding
 *
 * `Minecraft/09_Items_And_Inventory.md` §4: **container capacity NEVER GROWS**
 * across that entire game — ten years of updates and nothing holds more than a
 * double chest. What escalates is **portability, safety and automation**.
 *
 * ⚠️ *Inferred there, and worth keeping:* **a bigger box solves the storage
 * problem, and solving it deletes the building** — players build storage
 * *rooms*, and that is content.
 *
 * ## 🥫 No food, and it protects a block that does not exist yet
 *
 * **D71** says outright *"you can't store food in a chest"*, which is the entire
 * reason the **búr** (pantry) sits on **T135**. A chest that took food would
 * have **quietly deleted the búr before anybody built it.**
 *
 * ⚠️ **This is the rule players will bump into most**, and the one most likely
 * to want revisiting after a playtest. It is a decision, not a balance.
 *
 * ## ✅ What death does to a chest: nothing
 *
 * **D21** costs you everything you **carried**, and nothing in a container is
 * carried. 🔴 **D106 records that D82's *"the one place your death cannot
 * reach"* was therefore always vacuous** — a chest was exactly as safe as a
 * hearth and no code ever said otherwise. The hearth keeps the job that is real:
 * **it is where you wake up.**
 */

import { SPIN_INPUT, SPIN_OUTPUT, spinSlotAccepts } from './spin.ts';
import { LOOM_CLOTH, LOOM_THREAD, loomSlotAccepts } from './loomslots.ts';
import { edibleFor } from './items.ts';
import { canGoIn, isValidSlot, stackLimit, type Stack } from './inventory.ts';

/** The block key. */
export const CHEST_BLOCK = 'kistill';

/**
 * How many slots one chest holds.
 *
 * ⚠️ **A PLAYTEST number wearing Minecraft's clothes.** 27 is theirs; what was
 * taken from the research is that it **never grows**, not the digits.
 */
export const CHEST_SLOTS = 27;

/**
 * How many a double holds.
 *
 * ⚠️ **Not built yet, and deliberately second.** Two chests knowing they are one
 * needs connection state the game has none of. 🎯 haugur asked for it in the
 * same breath as the single — *"27 for a single chest, double that for a
 * double"* — and the single ships first.
 */
export const DOUBLE_CHEST_SLOTS = CHEST_SLOTS * 2;

/** What one chest is holding. */
export interface ChestState {
  readonly stacks: readonly Stack[];
}

/** An empty chest. */
export function emptyChest(): ChestState {
  return { stacks: [] };
}

/** Is this a slot a chest has? */
/**
 * 🏠 **What a container IS, in two numbers-and-a-rule** — so the storehouse
 * can be the chest's code with different answers rather than a second copy.
 *
 * | | slots | accepts |
 * |---|---|---|
 * | `kistill` | 27 | anything that is NOT food |
 * | `bur` | 54 | ONLY food — D71, D109: the one place food can live |
 *
 * 🔴 **One predicate, two blocks.** `edibleFor` is the test on both sides, so
 * the day something becomes food it moves from the chest's list to the búr's
 * without anybody editing either.
 */
export interface ContainerRules {
  readonly slots: number;
  readonly accepts: (itemKey: string) => boolean;
  /**
   * The sentence for a refused item — `en.json`'s key.
   *
   * ⚠️ **The skemma had the CHEST's sentence until 2026-09-04**, on the argument
   * that it is the same rule — and it is the same rule, but it is not the same
   * sentence: it says *"in a chest"* to somebody standing at a house. **The same
   * fault as the title, one screen over.**
   */
  readonly refusal:
    | 'chest.rejected.noFood'
    | 'bur.rejected.notFood'
    | 'skemma.rejected.noFood'
    | 'vefstofa.rejected.notWool'
    | 'vefstadur.rejected.notThread';
  /**
   * 🪧 **What this container is CALLED on its own screen** — `en.json`'s key.
   *
   * 🔴 **It lives here because the screen used to work it out from the SLOT
   * COUNT** — `capacity > 27 ? 'bur.title' : 'chest.title'` — **and the skemma
   * has 54 slots exactly as the búr does, so the store shed announced itself as
   * the food store.** 🎯 **haugur found it in play, 2026-09-04:** *"the menu for
   * skemma, it says Búr."*
   *
   * ⚠️ **The count was never the question.** Two containers can be the same size
   * and be different things, and the day a third one is 54 slots the old rule
   * would have been wrong again without anybody touching it. **A container names
   * itself here, beside the rule that says what it takes**, so the two can never
   * be answered from different places.
   */
  readonly title:
    'chest.title' | 'bur.title' | 'skemma.title' | 'vefstofa.title' | 'vefstadur.title';
  /**
   * 🧵 **May this item go into THIS slot?** — D127. Absent means any slot.
   *
   * ⚠️ **Only the vefstofa has one**: its slot 0 is wool and slot 1 is thread,
   * and {@link ContainerRules.accepts} cannot say which is which. **Asked on
   * top of `accepts`, never instead of it.**
   */
  readonly slotAccepts?: (slot: number, itemKey: string) => boolean;
}

export const CHEST_RULES: ContainerRules = {
  slots: CHEST_SLOTS,
  accepts: (itemKey) => edibleFor(itemKey) === undefined,
  refusal: 'chest.rejected.noFood',
  title: 'chest.title',
};

/** 🥫 The búr: twice the chest, and nothing but food. D109. */
export const BUR_RULES: ContainerRules = {
  slots: 54,
  accepts: (itemKey) => edibleFor(itemKey) !== undefined,
  refusal: 'bur.rejected.notFood',
  title: 'bur.title',
};

/**
 * 📦 **What the store shed takes — D110, 2026-09-04.**
 *
 * 🔴 **Everything EXCEPT food, and that is the whole reason it can exist.** A
 * store that took everything would do the búr's job and more, and nobody would
 * ever build two kinds of house. ⚠️ **It is {@link BUR_RULES} pointed the other
 * way**, and the same argument that made the chest refuse food to protect a búr
 * that did not exist yet.
 *
 * ⚠️ **The test is `edibleFor`, exactly as the chest's is**, so the day
 * something becomes edible it becomes un-storable here in the same change. **A
 * hard-coded list of food keys is the fault this project keeps paying for.**
 *
 * 👉 **It has its own refusal since 2026-09-04.** ⚠️ **It used to borrow the
 * chest's**, on the argument that it is the same sentence — and the rule is the
 * same, but the sentence was not: it told somebody standing in a house that food
 * *"will not keep in a chest"*. **The same fault as the title.**
 */
export const SKEMMA_RULES: ContainerRules = {
  slots: 54,
  accepts: (itemKey) => edibleFor(itemKey) === undefined,
  refusal: 'skemma.rejected.noFood',
  title: 'skemma.title',
};

/**
 * 🧵 **The textile room — D127, 2026-09-28.** Two slots: wool in, thread out.
 * `shared/src/spin.ts` turns one into the other over time.
 */
export const VEFSTOFA_RULES: ContainerRules = {
  slots: 2,
  accepts: (itemKey) => itemKey === SPIN_INPUT || itemKey === SPIN_OUTPUT,
  refusal: 'vefstofa.rejected.notWool',
  title: 'vefstofa.title',
  slotAccepts: spinSlotAccepts,
};

/**
 * 🧵 **The upright loom — D136, 2026-10-06.** Three slots: warp, weft, cloth.
 * `shared/src/loom.ts` hangs the warp and weaves the cloth over time.
 */
export const LOOM_RULES: ContainerRules = {
  slots: 3,
  accepts: (itemKey) => itemKey === LOOM_THREAD || itemKey === LOOM_CLOTH,
  refusal: 'vefstadur.rejected.notThread',
  title: 'vefstadur.title',
  slotAccepts: loomSlotAccepts,
};

/** 🧵 Does a container take this item in this particular slot? */
export function containerSlotAccepts(
  rules: ContainerRules,
  slot: number,
  itemKey: string,
): boolean {
  return rules.accepts(itemKey) && (rules.slotAccepts?.(slot, itemKey) ?? true);
}

export function isChestSlot(slot: number, rules: ContainerRules = CHEST_RULES): boolean {
  return Number.isInteger(slot) && slot >= 0 && slot < rules.slots;
}

/**
 * 🥫 **Would a chest take this item?**
 *
 * 🔴 **Food is refused — D106, protecting D71's búr.** ⚠️ The test is
 * `edibleFor`, the same field the eating path reads, so **the day something
 * becomes edible it becomes un-storable in the same change** and the two cannot
 * drift. A hard-coded list of food keys would have been the fault this project
 * keeps paying for.
 */
export function chestAccepts(itemKey: string): boolean {
  return edibleFor(itemKey) === undefined;
}

/** How many of `itemKey` this chest holds. */
export function chestCountOf(state: ChestState, itemKey: string): number {
  return state.stacks.reduce((n, s) => (s.itemKey === itemKey ? n + s.count : n), 0);
}

/** Everything in the chest, for tipping it into a pack when the block breaks. */
export function chestContents(state: ChestState): { itemKey: string; count: number }[] {
  /**
   * ⚠️ **Merged by item**, so five slots of móberg come back as one entry. The
   * caller pours this into a pack, and handing it the same key five times would
   * make five stacks where the player expects the fewest possible.
   */
  const byItem = new Map<string, number>();
  for (const s of state.stacks) byItem.set(s.itemKey, (byItem.get(s.itemKey) ?? 0) + s.count);
  return [...byItem].map(([itemKey, count]) => ({ itemKey, count }));
}

/** Is there room for `count` of `itemKey` somewhere in this chest? */
export function chestHasRoomFor(
  state: ChestState,
  itemKey: string,
  count = 1,
  rules: ContainerRules = CHEST_RULES,
): boolean {
  if (!rules.accepts(itemKey)) return false;
  const limit = stackLimit(itemKey);
  let room = 0;
  const used = new Set<number>();
  for (const s of state.stacks) {
    used.add(s.slot);
    if (s.itemKey === itemKey) room += limit - s.count;
  }
  for (let slot = 0; slot < rules.slots; slot++) {
    if (!used.has(slot)) room += limit;
  }
  return room >= count;
}

/**
 * Put items into a chest, filling part-stacks first and then empty slots.
 *
 * Returns what would not fit. 🔴 **Never clamped and never silently dropped** —
 * the same rule as the pack: a caller that ignores `leftOver` is the bug, and
 * hiding it would lose a player's things.
 */
export function addToChest(
  state: ChestState,
  itemKey: string,
  count: number,
  rules: ContainerRules = CHEST_RULES,
): { state: ChestState; leftOver: number } {
  if (!rules.accepts(itemKey) || !Number.isInteger(count) || count <= 0) {
    return { state, leftOver: count > 0 ? count : 0 };
  }

  const limit = stackLimit(itemKey);
  const bySlot = new Map<number, Stack>(state.stacks.map((s) => [s.slot, s]));
  let left = count;

  /* Top up what is already there, lowest slot first, so a chest fills tidily. */
  for (const slot of [...bySlot.keys()].sort((a, b) => a - b)) {
    if (left <= 0) break;
    const here = bySlot.get(slot);
    if (here === undefined || here.itemKey !== itemKey || here.count >= limit) continue;
    const room = Math.min(limit - here.count, left);
    bySlot.set(slot, { ...here, count: here.count + room });
    left -= room;
  }

  /* Then the empty slots. */
  for (let slot = 0; slot < rules.slots && left > 0; slot++) {
    if (bySlot.has(slot)) continue;
    /* 🧵 A vefstofa's thread never lands in its wool slot. */
    if (!containerSlotAccepts(rules, slot, itemKey)) continue;
    const put = Math.min(limit, left);
    bySlot.set(slot, { slot, itemKey, count: put });
    left -= put;
  }

  return {
    state: { stacks: [...bySlot.values()].sort((a, b) => a.slot - b.slot) },
    leftOver: left,
  };
}

/**
 * 📦 **One end of a move — which container, and which slot in it.**
 *
 * 🔴 **The container is NAMED rather than implied by the number**, for the
 * reason {@link HearthEnd} gives and which has not changed: both containers
 * start at slot 0, so a bare integer would mean two different boxes on two
 * different tables — and **the right count of the right item arrives in the
 * wrong place with nothing to show for it.**
 */
export interface ChestEnd {
  readonly where: 'chest' | 'player';
  /** 📦 A chest slot is 0–26. A player slot is 0–33. */
  readonly slot: number;
}

export interface ChestMoveResult {
  readonly state: ChestState;
  readonly stacks: readonly Stack[];
  /**
   * ⚠️ **`false` is not an error** — it is the answer to a move that is not
   * allowed, and the caller sends both sides back unchanged so the screen stops
   * disagreeing. 🔴 **Nothing is ever clamped to fit.**
   */
  readonly moved: boolean;
}

/** Read one slot out of a stack array. `at` rather than `[i]`, per the linter. */
function stackAt(stacks: readonly Stack[], slot: number): Stack | undefined {
  return stacks.find((s) => s.slot === slot);
}

/**
 * 📦 **Move `count` items between a player's pack and a chest** — D106, T135.
 *
 * ## 🔴 Why this is NOT `moveHearthItem` with a word changed
 *
 * A hearth slot holds **a count of one known thing**, so it can never swap:
 * whatever came out would have nowhere to go, and `moveHearthItem` refuses.
 * **A chest slot holds an arbitrary stack**, exactly as a pack slot does, so a
 * swap is not only possible but is what a player expects when they drop a
 * different item onto an occupied slot. ✅ **This follows `moveStack`'s rules
 * rather than the hearth's**, including *half a stack cannot be swapped*.
 *
 * ## ✅ Chest-to-chest is allowed, and the hearth's ban was not a principle
 *
 * `moveHearthItem` refuses both ends being the same container because
 * player-to-player is {@link moveStack}'s job and two functions half-answering
 * one question is how they drift. ⚠️ **Chest-to-chest has no such other owner**
 * — nothing else can tidy the inside of a chest — so it is allowed here, and
 * **player-to-player is still refused** and still `moveStack`'s.
 *
 * ## 🥫 Food is refused on the way IN, and only there
 *
 * **D106.** A move out of a chest never checks, because food can never have got
 * in — and a check there would be a second place for the rule to live.
 *
 * ⚠️ **A swap is a move in as well as out**, so the displaced item is checked
 * too. Without that, dropping a stone onto a chest slot would be a way to put
 * bread in one.
 */
export function moveChestItem(
  state: ChestState,
  stacks: readonly Stack[],
  from: ChestEnd,
  to: ChestEnd,
  count: number,
  rules: ContainerRules = CHEST_RULES,
): ChestMoveResult {
  const unchanged: ChestMoveResult = { state, stacks: [...stacks], moved: false };

  if (!Number.isInteger(count) || count <= 0) return unchanged;
  /* 🔴 Pack-to-pack belongs to moveStack. */
  if (from.where === 'player' && to.where === 'player') return unchanged;
  if (from.where === to.where && from.slot === to.slot) return unchanged;

  const ok = (end: ChestEnd): boolean =>
    end.where === 'chest' ? isChestSlot(end.slot, rules) : isValidSlot(end.slot);
  if (!ok(from) || !ok(to)) return unchanged;

  const source =
    from.where === 'chest' ? stackAt(state.stacks, from.slot) : stackAt(stacks, from.slot);
  if (source === undefined || source.count < count) return unchanged;

  const target = to.where === 'chest' ? stackAt(state.stacks, to.slot) : stackAt(stacks, to.slot);
  const remainder = source.count - count;
  const limit = stackLimit(source.itemKey);

  /**
   * Would `to` take this? 🥫 A chest asks {@link chestAccepts}; a player slot
   * asks `canGoIn`, which is what knows a hat cannot be worn on your feet.
   */
  const accepts = (end: ChestEnd, itemKey: string, n: number): boolean =>
    end.where === 'chest'
      ? containerSlotAccepts(rules, end.slot, itemKey)
      : canGoIn(end.slot, itemKey, n);

  if (!accepts(to, source.itemKey, count)) return unchanged;

  /* Both sides are worked on as maps and written back together, so a refusal
     part-way through cannot leave one side changed. */
  const chest = new Map<number, Stack>(state.stacks.map((s) => [s.slot, s]));
  const pack = new Map<number, Stack>(stacks.map((s) => [s.slot, s]));
  const put = (end: ChestEnd, stack: Stack | undefined): void => {
    const bag = end.where === 'chest' ? chest : pack;
    if (stack === undefined) bag.delete(end.slot);
    else bag.set(end.slot, stack);
  };

  if (target === undefined) {
    put(from, remainder > 0 ? { ...source, count: remainder } : undefined);
    put(to, { ...source, slot: to.slot, count });
  } else if (target.itemKey === source.itemKey) {
    const room = limit - target.count;
    if (room <= 0) return unchanged;
    const fits = Math.min(room, count);
    put(from, source.count - fits > 0 ? { ...source, count: source.count - fits } : undefined);
    put(to, { ...target, count: target.count + fits });
  } else {
    /* Two different items: a swap, and only ever a whole one — moveStack's rule
       and its reason, that half a stack has nowhere to put the other half. */
    if (remainder > 0) return unchanged;
    /* ⚠️ The displaced item has to be allowed where IT is going. 🥫 This is what
       stops a swap being a way to smuggle food into a chest. */
    if (!accepts(from, target.itemKey, target.count)) return unchanged;
    put(from, { ...target, slot: from.slot });
    put(to, { ...source, slot: to.slot });
  }

  const sorted = (m: Map<number, Stack>): Stack[] =>
    [...m.values()].sort((a, b) => a.slot - b.slot);
  return { state: { stacks: sorted(chest) }, stacks: sorted(pack), moved: true };
}

/**
 * 📦 **Take `count` of an item out of a container.**
 *
 * 🔴 **NOT {@link takeFromInventory}, and the difference is a real hazard.**
 * That one refuses to touch a **clothing slot**, which it decides by slot
 * NUMBER — 34 and above. **A búr has 54 slots**, so its slots 34 to 37 are
 * ordinary storage that the pack's rule would have silently skipped: the count
 * would say the wood was there and the take would come up short.
 *
 * ⚠️ **Highest slot first, exactly as the pack does it**, so a player watches
 * the far end of the container empty rather than the stack under their cursor.
 *
 * @returns the new contents and how many could not be found (0 when it all came
 *   out). ⚠️ **`shortBy > 0` means the caller must commit NOTHING** — the state
 *   handed back is a partial take and is only useful for saying how short.
 */
export function takeFromChest(
  state: ChestState,
  itemKey: string,
  count: number,
): { readonly state: ChestState; readonly shortBy: number } {
  if (count <= 0) return { state, shortBy: 0 };
  let left = count;
  const out: Stack[] = [];
  for (const stack of [...state.stacks].sort((a, b) => b.slot - a.slot)) {
    if (left === 0 || stack.itemKey !== itemKey) {
      out.push(stack);
      continue;
    }
    const taken = Math.min(stack.count, left);
    left -= taken;
    /* A slot taken to nothing leaves entirely rather than sitting as a zero —
       the same representation every other path in this file uses. */
    if (stack.count - taken > 0) out.push({ ...stack, count: stack.count - taken });
  }
  return { state: { stacks: out.sort((a, b) => a.slot - b.slot) }, shortBy: left };
}
